import { toISODate } from './dateRange';
import { toWholeCents } from './money';
import { expenseCategoryName } from './laborRules';

// Pure rules for the spreadsheet upload: reading the sheet rows, and deciding what an upload will do
// (add, update or skip) by comparing the file with what this business already has. No network here.
//
// MATCHING RULE (so uploading the same file twice, or an updated version, never doubles anything)
//   Jobs:    matched by name, ignoring capitals and extra spaces, among this business's uploaded jobs only
//            (QuickBooks jobs are never matched or changed). A match updates client, type and status when the
//            file has a value; a blank cell never erases what is saved.
//   Revenue: matched by job + date + description + invoice/doc number.
//   Expense: matched by job + date + description + vendor.
//   Rows are matched in two passes. Pass 1 uses the exact text above. Inside one match text, a file row is first
//   paired with a saved row of exactly the same amount (in cents), and only then are the leftovers paired in order,
//   so removing one of two similar rows from a file never rewrites the wrong one. Amount is NOT part of the match
//   text, so a corrected amount updates the saved row instead of adding a second one.
//   Pass 2 handles a fixed date: a file row that found no match is paired with the one saved row that has the same
//   job, description, doc number or vendor and the same amount in cents, whatever its date. That row gets the new
//   date (a blank date cell never erases a saved date). If more than one saved row fits, nothing is guessed and
//   the row is added.
//   If a description, doc number, vendor or the amount AND the date change together, the row looks new and is
//   added; the old row stays (a re-upload never deletes). To clean that up, delete the upload and import again.
//   Identical rows in one file (two $50 lines on one day) are both kept.
//   Expense category is saved in transactions.account_name (the label the Labor tab shows) so the owner's labor
//   choices and the keyword default apply to uploaded costs.

export function normText(v) {
  return String(v == null ? '' : v).trim().replace(/\s+/g, ' ').toLowerCase();
}

// Whole cents, never a float tail. Returns null when the cell is not a positive amount.
export function parseAmount(raw) {
  const text = String(raw == null ? '' : raw).replace(/[$,\s]/g, '');
  if (text === '') return null;
  const n = Number(text);
  if (!Number.isFinite(n) || n <= 0) return null;
  const cents = toWholeCents(n);
  return cents > 0 ? cents / 100 : null;
}

const cell = (row, ...names) => {
  for (const n of names) if (row[n] !== undefined && row[n] !== '' && row[n] !== null) return row[n];
  return '';
};

export function parseUploadRows({ jobRows = [], revRows = [], expRows = [] }) {
  const errors = [];
  const jobs = [];
  const seenJobs = new Map();
  let duplicateJobRows = 0;
  jobRows.forEach(row => {
    const name = String(cell(row, 'Job Name*', 'Job Name')).trim();
    if (!name) return;
    const job = {
      name,
      clientName: String(cell(row, 'Client Name')).trim(),
      jobType: String(cell(row, 'Job Type')).trim(),
      status: String(cell(row, 'Status')).trim(),
      startDate: toISODate(row['Start Date']),
      endDate: toISODate(row['End Date']),
    };
    const k = normText(name);
    if (seenJobs.has(k)) { // same job listed twice in the file: one job, later row's values win
      duplicateJobRows += 1;
      const prev = jobs[seenJobs.get(k)];
      ['clientName', 'jobType', 'status', 'startDate', 'endDate'].forEach(f => { if (job[f]) prev[f] = job[f]; });
      return;
    }
    seenJobs.set(k, jobs.length);
    jobs.push(job);
  });
  if (jobs.length === 0) errors.push('No jobs found in the Jobs tab.');
  const jobNames = new Set(jobs.map(j => normText(j.name)));

  let unreadableDates = 0;
  const readMoneyRows = (rows, label, extra) => {
    const out = [];
    rows.forEach((row, i) => {
      const jobName = String(cell(row, 'Job Name*', 'Job Name')).trim();
      const rawAmt = cell(row, 'Amount*', 'Amount');
      if (!jobName) {
        if (rawAmt !== '') errors.push(`${label} row ${i + 2}: no job name, so this row was skipped`);
        return;
      }
      const amount = parseAmount(rawAmt);
      if (amount === null) { errors.push(`${label} row ${i + 2}: invalid amount "${rawAmt}"`); return; }
      const matched = jobNames.has(normText(jobName));
      if (!matched) errors.push(`${label} row ${i + 2}: job "${jobName}" not found in Jobs tab`);
      const rawDate = cell(row, 'Date*', 'Date');
      const date = toISODate(rawDate);
      if (rawDate !== '' && !date) unreadableDates += matched ? 1 : 0;
      out.push({ jobName, matched, amount, date: date || null, dateUnreadable: rawDate !== '' && !date, ...extra(row) });
    });
    return out;
  };
  const revenue = readMoneyRows(revRows, 'Revenue', row => ({
    description: String(cell(row, 'Description')).trim(),
    docNumber: String(cell(row, 'Invoice/Doc#')).trim(),
  }));
  const expenses = readMoneyRows(expRows, 'Expense', row => ({
    vendor: String(cell(row, 'Vendor')).trim(),
    description: String(cell(row, 'Description*', 'Description')).trim(),
    category: String(cell(row, 'Category*', 'Category') || 'Other').trim(),
  }));
  return { jobs, revenue, expenses, errors, duplicateJobRows, unreadableDates };
}

const txnKey = (type, jobId, date, description, other) =>
  [type, jobId, date || '', normText(description), normText(other)].join('|');

// existingJobs: this business's uploaded jobs ({ id, name, client_name, job_type, status }).
// existingTxns: this business's uploaded transaction rows ({ id, job_id, type, amount, txn_date, description,
//               doc_number, vendor }).
// ids: { job(i), revenue(i), expense(i) } make ids for NEW rows (so the screen controls batch ids; tests are exact).
export function planUpload(parsed, existingJobs, existingTxns, ids, { withCategory = true } = {}) {
  const jobByName = new Map();
  (existingJobs || []).forEach(j => jobByName.set(normText(j.name), j));

  const jobInserts = [];
  const jobUpdates = [];
  const jobIdByName = {};
  let jobsSame = 0;
  parsed.jobs.forEach(j => {
    const k = normText(j.name);
    const ex = jobByName.get(k);
    if (ex) {
      jobIdByName[k] = ex.id;
      const patch = {};
      if (j.clientName && j.clientName !== (ex.client_name || '')) patch.client_name = j.clientName;
      if (j.jobType && j.jobType !== (ex.job_type || '')) patch.job_type = j.jobType;
      if (j.status && j.status !== (ex.status || '')) patch.status = j.status;
      if (Object.keys(patch).length) jobUpdates.push({ id: ex.id, patch }); else jobsSame += 1;
    } else {
      const id = ids.job(jobInserts.length);
      jobIdByName[k] = id;
      jobInserts.push({ id, name: j.name, client_name: j.clientName, job_type: j.jobType || 'General', status: j.status || 'In Progress' });
    }
  });

  const cents = (v) => toWholeCents(Number(v) || 0);
  const idParts = (id) => { const m = String(id).match(/^(.*?)(\d+)$/); return m ? [m[1], Number(m[2])] : [String(id), 0]; };
  const byId = (a, b) => {
    const [pa, na] = idParts(a.id), [pb, nb] = idParts(b.id);
    return pa < pb ? -1 : pa > pb ? 1 : na - nb;
  };
  const otherOf = (t) => (t.type === 'revenue' ? t.doc_number : t.vendor);
  const saved = [...(existingTxns || [])].sort(byId);
  const pool = new Map();
  saved.forEach(t => {
    const k = txnKey(t.type, t.job_id, t.txn_date, t.description, otherOf(t));
    if (!pool.has(k)) pool.set(k, []);
    pool.get(k).push(t);
  });

  // Every file money row, with its key.
  const fileRows = [];
  [['revenue', parsed.revenue, 'revenue'], ['expense', parsed.expenses, 'expenses']].forEach(([type, rows, bucket]) => {
    rows.filter(r => r.matched).forEach(r => {
      const jobId = jobIdByName[normText(r.jobName)];
      const other = type === 'revenue' ? r.docNumber : r.vendor;
      fileRows.push({ r, type, bucket, jobId, other, key: txnKey(type, jobId, r.date, r.description, other), match: null });
    });
  });

  const taken = new Set();
  // Pass 1: exact key. Same amount first, then the rest in order.
  const byKey = new Map();
  fileRows.forEach(f => { if (!byKey.has(f.key)) byKey.set(f.key, []); byKey.get(f.key).push(f); });
  byKey.forEach((group, k) => {
    const candidates = pool.get(k) || [];
    group.forEach(f => {
      const hit = candidates.find(t => !taken.has(t.id) && cents(t.amount) === cents(f.r.amount));
      if (hit) { taken.add(hit.id); f.match = hit; }
    });
    group.forEach(f => {
      if (f.match) return;
      const hit = candidates.find(t => !taken.has(t.id));
      if (hit) { taken.add(hit.id); f.match = hit; }
    });
  });
  // Pass 2: a corrected or removed date. Same job, text and amount in cents; used only when exactly one fits.
  fileRows.forEach(f => {
    if (f.match) return;
    const fits = saved.filter(t => !taken.has(t.id) && t.type === f.type && t.job_id === f.jobId
      && normText(t.description) === normText(f.r.description) && normText(otherOf(t)) === normText(f.other)
      && cents(t.amount) === cents(f.r.amount));
    if (fits.length === 1) { taken.add(fits[0].id); f.match = fits[0]; }
  });

  const txnInserts = [];
  const txnUpdates = [];
  const counts = { revenue: { added: 0, updated: 0, skipped: 0 }, expenses: { added: 0, updated: 0, skipped: 0 } };
  let undated = 0;
  const nextIdx = { revenue: 0, expense: 0 };
  fileRows.forEach(f => {
    const { r, type, bucket, jobId, match: ex } = f;
    const category = type === 'expense' && withCategory ? expenseCategoryName(r.category) : null;
    if (ex) {
      const patch = {};
      if (cents(ex.amount) !== cents(r.amount)) patch.amount = r.amount;
      if (r.date && r.date !== ex.txn_date) patch.txn_date = r.date;
      if (category && !ex.account_name) patch.account_name = category;
      if (!r.date && !ex.txn_date) undated += 1;
      if (Object.keys(patch).length) { txnUpdates.push({ id: ex.id, patch }); counts[bucket].updated += 1; }
      else counts[bucket].skipped += 1;
      return;
    }
    if (!r.date) undated += 1;
    const idFn = type === 'revenue' ? ids.revenue : ids.expense;
    const base = { id: idFn(nextIdx[type]++), job_id: jobId, type, amount: r.amount, txn_date: r.date || null, description: r.description || null };
    txnInserts.push(type === 'revenue' ? { ...base, doc_number: r.docNumber || null }
      : { ...base, vendor: r.vendor || null, ...(category ? { account_name: category } : {}) });
    counts[bucket].added += 1;
  });

  const summary = {
    added: jobInserts.length + counts.revenue.added + counts.expenses.added,
    updated: jobUpdates.length + counts.revenue.updated + counts.expenses.updated,
    skipped: jobsSame + (parsed.duplicateJobRows || 0) + counts.revenue.skipped + counts.expenses.skipped,
    undated,
    unreadableDates: parsed.unreadableDates || 0,
    jobs: { added: jobInserts.length, updated: jobUpdates.length, same: jobsSame },
    revenue: counts.revenue, expenses: counts.expenses,
  };
  return { jobInserts, jobUpdates, txnInserts, txnUpdates, summary };
}

// One plain line for the preview: "12 new, 3 updated, 0 duplicates skipped, 2 undated".
export function summaryLine(s) {
  return `${s.added} new, ${s.updated} updated, ${s.skipped} already imported, ${s.undated} undated`;
}

export function undatedNotice(s) {
  if (!s.undated) return '';
  const n = s.undated;
  return `${n} row${n === 1 ? ' has' : 's have'} no readable date. ${n === 1 ? 'It' : 'They'} will still be imported and count in all-time totals, but not when you filter by date range. Add a date in the file and upload it again to fix this; the rows will be updated, not doubled.`;
}
